/**
 * OperatorIdentificationSection - Configuração de identificação de operadores
 *
 * Permite habilitar/desabilitar a identificação do nome do operador
 * nas mensagens enviadas pelo canal.
 */

import React from 'react';
import type { UpdateChannelRequest } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons'
interface OperatorIdentificationSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: any) => void;
}

export const OperatorIdentificationSection: React.FC<OperatorIdentificationSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  const isEnabled = formData.identifyUser ?? channel.identifyUser ?? false;

  return (
    <Card>
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="user" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            Identificação de Operadores
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Toggle de Identificação */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="identify-user-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Identificar operador nas mensagens
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Quando habilitado, o nome do operador será adicionado ao início de cada mensagem enviada
            </p>
          </div>

          {/* Toggle Switch — shared 38×22 (F82) */}
          <span data-testid="identify-user-toggle">
            <Switch
              id="identify-user-toggle"
              checked={isEnabled}
              onChange={(checked) => onFieldChange('identifyUser', checked)}
            />
          </span>
        </div>

        {/* Explicação adicional quando habilitado */}
        {isEnabled && (
          <div
            className="mt-4 p-3 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-info-background)',
              borderColor: 'var(--theme-info-color)',
            }}
          >
            <div className="flex items-start gap-2">
              <Icon name="user" className="w-4 h-4 mt-0.5 flex-shrink-0"
                style={{ color: 'var(--theme-info-color)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  Como funciona
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                  Cada mensagem enviada por um operador será precedida pelo seu nome, por exemplo:
                  <br />
                  <span className="font-mono mt-1 block" style={{ color: 'var(--theme-card-text)' }}>
                    *João Silva:* Olá! Como posso ajudá-lo?
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Informação quando desabilitado */}
        {!isEnabled && (
          <div
            className="mt-4 p-3 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              As mensagens serão enviadas sem identificação do operador
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

OperatorIdentificationSection.displayName = 'OperatorIdentificationSection';
